import dayjs from "lib/dayjs";
import { axiosInstance } from "lib/axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { CategoryService } from "features/SourceSearch";
import { GroupService } from "features/Group";
import {
  setAcceptedMISTerms,
  setAvatarProps,
  setIsCategoriesSelected,
} from "context/features/dataSlice";
import { loginUser } from "context/features/authSlice";

import {
  setCategoriesData,
  setAccountExpiration,
  setAccountType,
  setAutoRenew,
  setBillingHistory,
  setCurrentPayment,
  setSubscriptionId,
  setGroupId,
  setGradeLicenseLevel,
  setGroupRole,
  setInit,
  setIsHomeschool,
  setTimeTillExpired,
  setRegistrationDate,
  setGroupPreferences,
  setGroupName,
  setGroupAdmins,
} from "../context";
import { SubscriptionService, UserInfoService } from "../services";
import { BillingHistoryRow } from "../models";
import { toast } from "react-toastify";
import { updateAvatar } from "features/Settings/services/avatar.service";
import {
  setPublisherDetails,
  setPublisherId,
  setPublisherName,
  setPublisherRole,
  setRoles,
} from "features/PublisherAnalytics/context/publisherSlice";

export function useUserData() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { getCategories } = CategoryService(axiosInstance);
  const userInfoService = new UserInfoService(axiosInstance);
  const subService = new SubscriptionService(axiosInstance);
  const groupService = new GroupService(axiosInstance);

  const { user, isAuthenticated, userLoading } = useSelector(
    (state) => state.auth
  );
  const { categories } = useSelector((state) => state.profile);
  const {
    accountExpiration,
    accountType,
    autoRenew,
    billingHistory,
    currentPayment,
    gradeLicenseLevel,
    groupAdmins,
    groupId,
    groupName,
    groupPreferences,
    groupRole,
    init,
    isHomeschool,
    registrationDate,
    subscriptionId,
    timeTillExpired,
  } = useSelector((state) => state.account);

  // const [timeTillExpired, setTimeTillExpired] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(0);

  const isSubscriptionLoading = useMemo(() => {
    if (user == null) {
      return false;
    }
    return !init || subscriptionLoading > 0;
  }, [subscriptionLoading, init]);

  const showSubscriptions = useMemo(
    () => groupId == null || groupRole === "OrganizationAdmin",
    [groupId, groupRole]
  );

  function handleLoading(isLoading) {
    if (isLoading) {
      setSubscriptionLoading(subscriptionLoading + 1);
    } else {
      setSubscriptionLoading(
        subscriptionLoading > 0 ? subscriptionLoading - 1 : 0
      );
    }
  }

  const checkUserFromStorage = () => {
    if (sessionStorage.getItem("user")) {
      return JSON.parse(sessionStorage.getItem("user"));
    } else {
      return JSON.parse(localStorage.getItem("user"));
    }
  };

  const updateReduxState = (newUser, invalid) => {
    if (invalid) {
      dispatch(loginUser({ user: newUser }));
    } else {
      let userFromStorage = checkUserFromStorage();
      if (
        !user ||
        (userFromStorage && user.userId !== userFromStorage.userId)
      ) {
        dispatch(loginUser({ user: userFromStorage }));
      }
    }
  };

  const calculateTimeTillExpired = useCallback(
    (simulation) => {
      let now = dayjs();
      if (simulation != null) {
        now = dayjs(simulation).isValid() ? dayjs(simulation) : dayjs();
      }
      const expirationDate = dayjs(accountExpiration);
      return expirationDate.diff(now, "day") >= 0
        ? expirationDate.diff(now, "day")
        : 0;
    },
    [accountExpiration]
  );

  const isSubscriptionExpired = useMemo(() => {
    let now = dayjs();
    if (searchParams.has("simulation")) {
      const simulation = searchParams.get("simulation");
      now = dayjs(simulation).isValid() ? dayjs(simulation) : dayjs();
    }
    const expirationDate = dayjs(accountExpiration).endOf("day");
    const isGracePeriod = expirationDate.diff(now) >= 0;

    if (searchParams.has("simulation")) {
      const simTimeTillExp = calculateTimeTillExpired(
        searchParams.get("simulation")
      );
      return !isSubscriptionLoading && simTimeTillExp <= 0 && !isGracePeriod;
    }
    return (
      !isSubscriptionLoading &&
      timeTillExpired != null &&
      timeTillExpired <= 0 &&
      !isGracePeriod
    );
  }, [
    isSubscriptionLoading,
    timeTillExpired,
    accountExpiration,
  ]);

  async function setCategories(userId) {
    try {
      const data = await getCategories(userId);

      dispatch(setCategoriesData(data));
    } catch (e) {
      console.error(e);
    }
  }

  async function getSubscriptionInfo() {
    if (!isAuthenticated) {
      setInit(true);
      return;
    }
    handleLoading(true);
    try {
      const accountData = await subService.getIndividualSubscriptionData();

      dispatch(setAccountType(accountData.planType?.toLowerCase()));
      dispatch(setAccountExpiration(accountData.expirationDate));
      dispatch(setAutoRenew(accountData.recurring));

      dispatch(
        setCurrentPayment(
          accountData?.paymentMethod == null
            ? null
            : `${accountData?.paymentMethod?.toUpperCase()}${accountData.cardLast4 != null
              ? ` *${accountData.cardLast4.toUpperCase()}`
              : ""
            }`
        )
      );
      dispatch(setSubscriptionId(accountData.subscriptionId));
      const history = Array.isArray(accountData.billingHistory)
        ? accountData.billingHistory.map((item) => new BillingHistoryRow(item))
        : [];
      dispatch(setBillingHistory(history));
      if (!init) {
        dispatch(setInit(true));
      }
    } catch (error) {
      console.error(error);
    } finally {
      handleLoading(false);
    }
  }

  async function getUserInfo() {
    handleLoading(true);
    try {
      const { userInfo: user, rawData } = await userInfoService.getUserInfo();

      if (rawData?.accountType === 2) {
        dispatch(setIsHomeschool(true));
      } else {
        dispatch(setIsHomeschool(false));
      }

      if (user.publisherRole) {
        dispatch(setPublisherId(user.publisherDetails[0]?.publisherId));
        dispatch(setPublisherName(user.publisherDetails[0]?.publisherName));
        dispatch(setPublisherRole(user.publisherRole));
        dispatch(setPublisherDetails(user.publisherDetails));

        // Only if the stored publisher is not in the current user's publisher list
        const currentStoredId = localStorage.getItem(`sooth-publisherId`);
        const isCurrentSelectionValid = user.publisherDetails.some(p =>
          p.publisherId == currentStoredId || p.sourcesId == currentStoredId
        );
        console.log(isCurrentSelectionValid, currentStoredId);

        if (!isCurrentSelectionValid || !currentStoredId) {
          localStorage.setItem(`sooth-publisherId`, user.publisherDetails[0]?.publisherId);
          localStorage.setItem(`sooth-publisherName`, user.publisherDetails[0]?.publisherName);
        }
      }
      dispatch(setRoles(user.roles));
      dispatch(setAcceptedMISTerms(user.acceptedMisTerms));
      dispatch(setIsCategoriesSelected(user.isCategoriesSelected));
      dispatch(
        setGroupAdmins(Array.isArray(user.groupAdmins) ? user.groupAdmins : [])
      );
      dispatch(setGroupId(user.groupId));
      dispatch(setGroupName(user.groupName));
      dispatch(setGradeLicenseLevel(user.gradeLicenseLevel));
      dispatch(setGroupRole(user.groupRole));
      dispatch(setRegistrationDate(user.createdDate));
      dispatch(setGroupPreferences(user.groupPreferences));
      dispatch(setAvatarProps(user.userAvatar));
      return user;
    } catch (error) {
      console.error(error);
    } finally {
      handleLoading(false);
    }
  }

  async function updateGroupLicense(groupLicenseKey) {
    try {
      await userInfoService.updateGradeLicenseKey(groupLicenseKey, user.userId);
      dispatch(setGradeLicenseLevel(groupLicenseKey));
    } catch (error) {
      console.error(error);
    }
  }

  async function joinGroup(code) {
    try {
      const response = await groupService.joinGroupCode(code);
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function updateUserAvatar({ userAvatar }) {
    try {
      const { avatarSchema, avatarConfig } = userAvatar;
      const response = await updateAvatar(avatarSchema, avatarConfig);
      dispatch(setAvatarProps(response));
      toast.success("Avatar updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong, try again later");
    }
  }

  useEffect(() => {
    dispatch(setTimeTillExpired(calculateTimeTillExpired()));
    const interval = setInterval(() => {
      const calcedTime = calculateTimeTillExpired();
      if (calcedTime !== timeTillExpired) {
        dispatch(setTimeTillExpired(calcedTime));
      }
    }, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [calculateTimeTillExpired, timeTillExpired, dispatch]);

  return {
    accountExpiration,
    accountType,
    autoRenew,
    billingHistory,
    categories,
    checkUserFromStorage,
    currentPayment,
    getSubscriptionInfo,
    getUserInfo,
    gradeLicenseLevel,
    groupAdmins,
    groupId,
    groupName,
    groupPreferences,
    groupRole,
    isAuthenticated,
    isHomeschool,
    isSubscriptionExpired,
    isSubscriptionLoading,
    joinGroup,
    registrationDate,
    setCategories,
    showSubscriptions,
    subscriptionId,
    timeTillExpired,
    updateGroupLicense,
    updateReduxState,
    user,
    userLoading,
    updateUserAvatar,
  };
}
